import { Link } from 'react-router-dom';
import { LEGAL_EFFECTIVE_DATE, PUBLISHER } from './legal-facts';
import { LegalShell, List, Mail, Section } from './LegalShell';

/**
 * Fint's terms of service.
 *
 * The commercial choices here were the owner's, made on 14 Oct 2026: free
 * during early access with paid plans announced 30 days ahead; monthly plans,
 * cancel any time, no part-period refunds; 18 or over, any country; Indian
 * law and the courts at Chennai; a firm "this is not professional advice",
 * which the CFO page's suggestions and AI answers made necessary. The
 * liability cap and the acceptable-use list are a first draft for the
 * owner's review, not settled positions.
 */
export function TermsPage() {
  return (
    <LegalShell title="Terms of service" updated={LEGAL_EFFECTIVE_DATE}>
      <p>
        These terms are the agreement between you and {PUBLISHER.name} (&ldquo;we&rdquo;) for using Fint. By creating an
        account or using Fint you accept them. If you use Fint for a business, you accept them for that business and
        confirm you may do so.
      </p>

      <Section title="1. Who can use Fint">
        <p>You must be 18 or over. Fint is built for businesses in India, but anyone who meets these terms may use it.</p>
      </Section>

      <Section title="2. Your account">
        <List>
          <li>Keep your password to yourself, and turn on two-step sign-in if you can.</li>
          <li>
            You are responsible for what happens under your account, including what the people you invite and the assistants
            you connect do with the access you give them.
          </li>
          <li>
            Tell us at <Mail /> straight away if you think someone else has got into your account.
          </li>
        </List>
      </Section>

      <Section title="3. Your data stays yours">
        <p>
          You own what you put into Fint. You let us store, process and display it only so that Fint works for you and the
          people you share it with. You confirm you are allowed to put into Fint the details of your customers, vendors and
          team, and that you will use them lawfully. Our{' '}
          <Link className="text-primary hover:underline" data-tap-exempt="text link in a sentence" to="/privacy">
            privacy policy
          </Link>{' '}
          explains how we handle it.
        </p>
      </Section>

      <Section title="4. Fint is a tool, not your accountant">
        <p>
          Fint works out figures, returns, reminders and suggestions from what is recorded in it. Its suggestions, including
          the CFO page and any answer written by an AI assistant, are <strong>information, not tax, legal, financial or
          investment advice</strong>. Figures are only as right as the records behind them, and tax rules change.
        </p>
        <p>
          Check anything you file, pay or sign with a qualified professional, such as your chartered accountant. You are
          responsible for your returns, your filings, your payments and your decisions.
        </p>
      </Section>

      <Section title="5. AI assistants">
        <p>
          If you connect an AI assistant, it acts on your instructions and under its provider&rsquo;s terms. Its answers can
          be wrong. Fint checks the amounts in an assistant&rsquo;s answer against its own figures before showing it, but
          cannot guarantee what an assistant writes. An assistant can propose records; nothing it proposes is recorded, sent
          to a customer or paid until a person in your business does that in Fint.
        </p>
      </Section>

      <Section title="6. What you must not do">
        <List>
          <li>Use Fint for anything unlawful, including false invoices, fake input tax credit or hiding income.</li>
          <li>Upload malware, or try to get into accounts, books or systems that are not yours.</li>
          <li>Overload Fint, or scrape it beyond normal use of its screens and its connector.</li>
          <li>Resell or rent out access to Fint without our written permission.</li>
          <li>Use Fint to send messages your recipients did not expect and would not want.</li>
        </List>
      </Section>

      <Section title="7. Price and payment">
        <p>
          Fint is free during early access. We will introduce paid plans, and we will tell you the price at least 30 days
          before any charge applies to you. If you do not want to pay, you can download your books and leave before then.
        </p>
        <p>
          Paid plans will be billed monthly in advance through Razorpay, with GST added where it applies. You can cancel at
          any time: cancelling stops the next charge, and the plan runs to the end of the period already paid.{' '}
          <strong>We do not refund part periods.</strong>
        </p>
      </Section>

      <Section title="8. Availability and backups">
        <p>
          We work to keep Fint running and correct, but it is provided as it is, and it will sometimes be unavailable or
          wrong. We do not keep separate backups of the database at present, so keep your own copies: download your books
          regularly from Settings &rarr; Your account.
        </p>
      </Section>

      <Section title="9. Ending the agreement">
        <List>
          <li>
            You can delete your account at any time in Settings. You then have 30 days to change your mind or download your
            books before they are deleted.
          </li>
          <li>
            We may suspend or close an account that breaks these terms or the law, or that puts other users at risk. Unless
            the law or the risk prevents it, we will tell you why and give you 30 days to download your books.
          </li>
        </List>
      </Section>

      <Section title="10. Liability">
        <p>
          We are not liable for indirect or consequential loss, such as lost profit, penalties or interest charged to you by a
          tax authority, or loss of data you could have kept a copy of. Our total liability to you for any claim is limited
          to what you paid us for Fint in the 12 months before the claim, or &#8377;1,000 if you paid nothing. Nothing here
          limits a liability that the law does not allow to be limited.
        </p>
        <p>
          If a claim is brought against us because of what you put into Fint or how you used it in breach of these terms,
          you will cover our reasonable costs of it.
        </p>
      </Section>

      <Section title="11. Changes to these terms">
        <p>
          If we change these terms in a way that matters to you, we will tell you by email or in Fint at least 30 days before
          the change applies. Using Fint after that means you accept the new terms; if you do not, you can leave and take
          your books with you.
        </p>
      </Section>

      <Section title="12. Law and disputes">
        <p>
          These terms are governed by the laws of India. Disputes go to the courts at {PUBLISHER.courts}, Tamil Nadu. Please
          write to us first: most things are sorted out faster that way.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          {PUBLISHER.name}, {PUBLISHER.address.join(', ')}. Email <Mail />.
        </p>
      </Section>
    </LegalShell>
  );
}
